import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '../../../context/ThemeContext'

// Pre-login landing page for an admin plan-grant checkout link emailed to the
// user: ${FRONTEND_PORTAL_URL}/auth/billing/plan-grant/checkout?token=<jwt>.
//
// The token is UX-only (no verification): we base64-decode the payload to
// prefill the sign-in email and stash the post-login destination. It may be
// absent or expired (30-day TTL) — the page must still work; the user just signs
// in normally. The trusted plan details + payment happen after login via
// GET /api/v3/subscription/plan-grant and the pay endpoint.

interface TokenPayload {
  id?: string
  email?: string
  planId?: string
  grantId?: string
  exp?: number
}

const decodeJwtPayload = (token: string): TokenPayload | null => {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    )
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const PlanGrantInvite: React.FC = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const dark = theme === 'dark'

  const token = params.get('token') || ''
  const payload = token ? decodeJwtPayload(token) : null
  const expired = payload?.exp ? payload.exp * 1000 < Date.now() : false
  const email = !expired ? payload?.email : undefined

  useEffect(() => {
    // After login, send the user to the real (authenticated) checkout. Consumed
    // by PostLoginRedirect in AppRoutes.
    sessionStorage.setItem('postLoginRedirect', '/billing/plan-grant/checkout')
    if (email) sessionStorage.setItem('planGrantEmail', email)
  }, [email])

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <div
        className="card "
        style={{ maxWidth: 480, width: '100%', ...(dark ? { backgroundColor: '#2C3A4D', borderColor: '#3a4a5e' } : {}) }}
      >
        <div className="card-body text-center p-8">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-5"
            style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'rgba(255,199,0,0.15)' }}
          >
            <i className="bi bi-stars text-warning" style={{ fontSize: 'var(--icon-3xl)' }} />
          </div>

          <h2 className={`fw-bold mb-3 ${dark ? 'white__text' : 'text-gray-900'}`}>
            Complete your purchase
          </h2>
          <p className={`fs-6 mb-2 ${dark ? '' : 'text-gray-600'}`} style={dark ? { color: '#cbd5e1' } : {}}>
            You&apos;ve been invited to activate access on your B4igo account. Sign in to make your
            one-time payment and unlock it — access never expires.
          </p>

          {email && (
            <p className="fs-7 mb-5" style={{ color: dark ? '#9ca3af' : '#7e8299' }}>
              Continue as <strong>{email}</strong>
            </p>
          )}
          {!email && <div className="mb-5" />}

          <button type="button" className="btn btn-primary w-100" onClick={() => navigate('/auth/login')}>
            <i className="bi bi-box-arrow-in-right me-2" />
            Sign in to continue
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlanGrantInvite
